import React, { useCallback, useMemo } from 'react';
import { useWatch } from 'react-hook-form';
import type { DataTableRenderCellParams, DataTableRowEventHandler } from '@/components/custom/erp/data-table-v2';
import { useInvoiceHydration } from '@/hooks/queries/useInvoiceHydration';
import { ErpInputTableV2 } from '@/components/custom/erp';
import { SelectedCellInfo } from '../hooks/useDetailRows';
import { getDetailTableColumns } from './columns';
import { FormMode } from '@/types/form';

interface DetailTabProps {
  formMode: FormMode;
  rows: { uuid?: string | null }[];
  selectedRowUuid?: string | null;
  selectedCell?: SelectedCellInfo | null;
  onRowClick?: DataTableRowEventHandler<MuaHangRecord>;
  onCellClick?: (params: DataTableRenderCellParams<MuaHangRecord>) => void;
  onAddRow: () => void;
  onDeleteRow: () => void;
  onCopyRow: () => void;
  onPasteRow: (clipboardText?: string) => void | Promise<void>;
  onMoveRow: (direction: 'up' | 'down') => void;
  onCellValueChange: (rowUuid: string, field: string, newValue: MuaHangRecord) => void;
  loaiChungTu: string;
}

const DETAIL_ACTION_ORDER = [
  'add',
  'delete',
  'copy',
  'paste',
  'moveUp',
  'moveDown',
  'export',
  'pin',
  'viewInventory',
  'viewReceipt'
] as const;

const noop = () => {};

export const DetailTab: React.FC<DetailTabProps> = ({
  formMode,
  rows,
  selectedRowUuid,
  selectedCell,
  onRowClick,
  onCellClick,
  onAddRow,
  onDeleteRow,
  onCopyRow,
  onPasteRow,
  onMoveRow,
  onCellValueChange,
  loaiChungTu
}) => {
  const [dien_giai] = useWatch({ name: ['dien_giai'] });
  useInvoiceHydration({
    rows: rows as MuaHangRecord[],
    enabled: formMode !== 'add',
    onHydrated: (uuid, invoice) => onCellValueChange(uuid, 'id_hd_data', invoice as MuaHangRecord)
  });
  const columns = useMemo(
    () => getDetailTableColumns(onCellValueChange, loaiChungTu, dien_giai),
    [dien_giai, loaiChungTu, onCellValueChange]
  );
  const handleRowId = useCallback((row: MuaHangRecord) => row?.uuid || '', []);
  const tableActions = useMemo(
    () => ({
      add: onAddRow,
      delete: onDeleteRow,
      copy: onCopyRow,
      paste: onPasteRow,
      moveUp: () => onMoveRow('up'),
      moveDown: () => onMoveRow('down'),
      export: noop,
      pin: noop,
      viewInventory: noop,
      viewReceipt: noop
    }),
    [onAddRow, onCopyRow, onDeleteRow, onMoveRow, onPasteRow]
  );

  return (
    <ErpInputTableV2
      rows={rows}
      onRowClick={onRowClick as MuaHangRecord}
      onCellClick={onCellClick as MuaHangRecord}
      selectedRowId={selectedRowUuid || undefined}
      columns={columns}
      getRowId={handleRowId}
      pasteTarget={selectedCell}
      actionsDisabled={formMode === 'view'}
      actionOrder={DETAIL_ACTION_ORDER as MuaHangRecord}
      actions={tableActions}
    />
  );
};
